import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
} from 'lucide-react';

interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
}

const pad = (value: number) => String(value).padStart(2, '0');
const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const toLocalDateTimeValue = (date: Date): string => {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const parseLocalDateTimeValue = (value: string): Date | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return null;

  const [, year, month, day, hour, minute] = match;
  const parsed = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const roundUpToCurrentMinute = (date: Date): Date => {
  const rounded = new Date(date);
  if (rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0) {
    rounded.setMinutes(rounded.getMinutes() + 1);
  }
  rounded.setSeconds(0, 0);
  return rounded;
};

const getMonthDays = (month: Date): Date[] => {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  return Array.from(
    { length: daysInMonth },
    (_, index) => new Date(year, monthIndex, index + 1),
  );
};

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const getEndOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const formatDisplayValue = (value: string): string => {
  const parsed = parseLocalDateTimeValue(value);
  if (!parsed) return '';

  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  ariaLabel,
  placeholder = 'Select date and time',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draftDate, setDraftDate] = useState<Date>(() =>
    parseLocalDateTimeValue(value) ?? roundUpToCurrentMinute(new Date()),
  );
  const [visibleMonth, setVisibleMonth] = useState<Date>(
    () => new Date(draftDate.getFullYear(), draftDate.getMonth(), 1),
  );
  const [hour, setHour] = useState(draftDate.getHours());
  const [minute, setMinute] = useState(draftDate.getMinutes());

  const selectedValue = formatDisplayValue(value);
  const now = roundUpToCurrentMinute(new Date());

  const candidate = useMemo(
    () =>
      new Date(
        draftDate.getFullYear(),
        draftDate.getMonth(),
        draftDate.getDate(),
        hour,
        minute,
      ),
    [draftDate, hour, minute],
  );
  const isPastCandidate = candidate.getTime() < now.getTime();

  const resetDraft = () => {
    const parsedValue = parseLocalDateTimeValue(value);
    const base =
      parsedValue && parsedValue.getTime() >= now.getTime()
        ? parsedValue
        : now;

    setDraftDate(base);
    setVisibleMonth(new Date(base.getFullYear(), base.getMonth(), 1));
    setHour(base.getHours());
    setMinute(base.getMinutes());
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const days = getMonthDays(visibleMonth);
  const leadingSlots = days[0]?.getDay() ?? 0;
  const monthTitle = visibleMonth.toLocaleDateString([], {
    month: 'long',
    year: 'numeric',
  });

  const setMonth = (offset: number) => {
    setVisibleMonth(
      new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + offset, 1),
    );
  };

  const handleOpen = () => {
    resetDraft();
    setOpen((current) => !current);
  };

  const handleDateSelect = (day: Date) => {
    if (getEndOfDay(day).getTime() < now.getTime()) {
      return;
    }

    setDraftDate(day);
  };

  const handleApply = () => {
    if (candidate.getTime() < roundUpToCurrentMinute(new Date()).getTime()) {
      return;
    }

    onChange(toLocalDateTimeValue(candidate));
    setOpen(false);
  };

  const handleTimeTextChange = (
    rawValue: string,
    max: number,
    setter: React.Dispatch<React.SetStateAction<number>>,
  ) => {
    setter(clamp(Number(rawValue) || 0, 0, max));
  };

  const handlePresetSelect = (daysFromNow: number) => {
    const preset = roundUpToCurrentMinute(new Date());
    preset.setDate(preset.getDate() + daysFromNow);

    setDraftDate(preset);
    setVisibleMonth(new Date(preset.getFullYear(), preset.getMonth(), 1));
    setHour(preset.getHours());
    setMinute(preset.getMinutes());
  };

  return (
    <div
      ref={containerRef}
      className={`date-time-picker ${open ? 'open' : ''}`.trim()}
    >
      <button
        type="button"
        className="date-time-picker__trigger"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={handleOpen}
      >
        <span className={selectedValue ? '' : 'date-time-picker__placeholder'}>
          {selectedValue || placeholder}
        </span>
        <Calendar className="date-time-picker__trigger-icon" />
      </button>

      {open && (
        <div className="date-time-picker__panel">
          <div className="date-time-picker__content">
            <div
              className="date-time-picker__date-section"
              role="group"
              aria-label="Date options"
            >
              <div className="date-time-picker__header">
                <button
                  type="button"
                  className="date-time-picker__month-button"
                  aria-label="Previous month"
                  onClick={() => setMonth(-1)}
                >
                  <ChevronLeft />
                </button>
                <span>{monthTitle}</span>
                <button
                  type="button"
                  className="date-time-picker__month-button"
                  aria-label="Next month"
                  onClick={() => setMonth(1)}
                >
                  <ChevronRight />
                </button>
              </div>

              <div className="date-time-picker__weekdays" aria-hidden="true">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>

              <div className="date-time-picker__grid">
                {Array.from({ length: leadingSlots }).map((_, index) => (
                  <span key={`empty-${index}`} />
                ))}
                {days.map((day) => {
                  const disabled = getEndOfDay(day).getTime() < now.getTime();
                  const selected = isSameDay(day, draftDate);
                  const label = day.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  });

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      aria-label={label}
                      className={selected ? 'selected' : ''}
                      disabled={disabled}
                      onClick={() => handleDateSelect(day)}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              className="date-time-picker__time"
              role="group"
              aria-label="Time options"
            >
              <div className="date-time-picker__time-heading">
                <Clock />
                <span>Time</span>
              </div>
              <div className="date-time-picker__time-inputs">
                <label>
                  Hour
                  <span className="date-time-picker__time-control">
                    <input
                      aria-label="Hour"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={pad(hour)}
                      onChange={(event) =>
                        handleTimeTextChange(event.target.value, 23, setHour)
                      }
                    />
                    <span className="date-time-picker__stepper">
                      <button
                        type="button"
                        aria-label="Increase hour"
                        onClick={() => setHour((current) => clamp(current + 1, 0, 23))}
                      >
                        <ChevronUp />
                      </button>
                      <button
                        type="button"
                        aria-label="Decrease hour"
                        onClick={() => setHour((current) => clamp(current - 1, 0, 23))}
                      >
                        <ChevronDown />
                      </button>
                    </span>
                  </span>
                </label>
                <span className="date-time-picker__separator">:</span>
                <label>
                  Minute
                  <span className="date-time-picker__time-control">
                    <input
                      aria-label="Minute"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={pad(minute)}
                      onChange={(event) =>
                        handleTimeTextChange(event.target.value, 59, setMinute)
                      }
                    />
                    <span className="date-time-picker__stepper">
                      <button
                        type="button"
                        aria-label="Increase minute"
                        onClick={() =>
                          setMinute((current) => clamp(current + 1, 0, 59))
                        }
                      >
                        <ChevronUp />
                      </button>
                      <button
                        type="button"
                        aria-label="Decrease minute"
                        onClick={() =>
                          setMinute((current) => clamp(current - 1, 0, 59))
                        }
                      >
                        <ChevronDown />
                      </button>
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {isPastCandidate && (
            <p className="date-time-picker__error">
              Choose a future date and time.
            </p>
          )}

          <div className="date-time-picker__presets">
            <button type="button" onClick={() => handlePresetSelect(1)}>
              Tomorrow
            </button>
            <button type="button" onClick={() => handlePresetSelect(3)}>
              3 Days
            </button>
            <button type="button" onClick={() => handlePresetSelect(7)}>
              7 Days
            </button>
          </div>

          <div className="date-time-picker__actions">
            <button
              type="button"
              className="date-time-picker__secondary"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="date-time-picker__primary"
              disabled={isPastCandidate}
              onClick={handleApply}
            >
              Apply date and time
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;
